import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { useInstructorMode } from '../hooks/useInstructorMode';
import './Stats.css';

const API_BASE = process.env.REACT_APP_API_URL || '/api';

function Stats() {
  const { buildUrl } = useInstructorMode();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchStats = async () => {
      try {
        setLoading(true);
        const response = await axios.get(`${API_BASE}/stats`);
        setStats(response.data);
        setError(null);
      } catch (err) {
        setError(err.response?.data?.error || 'Failed to load stats');
        console.error('Error fetching stats:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchStats();
  }, []);

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const formatArtists = (artists) => {
    if (!artists || artists.length === 0) return 'Unknown';
    return artists.filter(a => a).join(', ');
  };

  const formatNumber = (num) => {
    if (num == null) return '0';
    return num.toLocaleString();
  };

  // Mini table component for top 5 lists
  const MiniTable = ({ title, icon, items, labelKey = 'name', countKey = 'count', linkPrefix = '/song' }) => (
    <div className="mini-table">
      <h4>{icon} {title}</h4>
      <table>
        <tbody>
          {items?.map((item, idx) => (
            <tr key={item.id || idx}>
              <td className="rank">{idx + 1}</td>
              <td className="name">
                <Link to={buildUrl(`${linkPrefix}/${item.id}`)}>{item[labelKey]}</Link>
                {item.artists && <span className="artist-hint">{formatArtists(item.artists)}</span>}
              </td>
              <td className="count">{formatNumber(parseInt(item[countKey]))}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );

  if (loading) {
    return (
      <div className="stats-page">
        <div className="loading">
          <div className="spinner"></div>
          <p>Crunching the numbers...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="stats-page error">
        <h2>Error</h2>
        <p>{error}</p>
      </div>
    );
  }

  return (
    <div className="stats-page">
      <div className="stats-header">
        <h1>📊 Fun Stats</h1>
        <p className="subtitle">Your cycling music by the numbers</p>
      </div>

      {/* Big Numbers */}
      <div className="stats-section big-numbers">
        <div className="stat-card">
          <div className="stat-number">{stats.counts?.total_songs?.toLocaleString()}</div>
          <div className="stat-label">Total Songs</div>
        </div>
        <div className="stat-card">
          <div className="stat-number">{stats.counts?.total_artists?.toLocaleString()}</div>
          <div className="stat-label">Artists</div>
        </div>
        <div className="stat-card">
          <div className="stat-number">{stats.counts?.total_playlists?.toLocaleString()}</div>
          <div className="stat-label">Playlists</div>
        </div>
        <div className="stat-card">
          <div className="stat-number">{stats.counts?.total_plays?.toLocaleString()}</div>
          <div className="stat-label">Total Plays</div>
        </div>
      </div>

      {/* Hall of Fame - Mini Tables */}
      <div className="stats-section">
        <h2>🏆 Hall of Fame</h2>
        <div className="mini-tables-grid">
          {stats.topSongs && (
            <MiniTable 
              title="Most Played Songs" 
              icon="👑" 
              items={stats.topSongs} 
              labelKey="name" 
              countKey="plays" 
              linkPrefix="/song"
            />
          )}
          {stats.topArtists && (
            <MiniTable 
              title="Most Played Artists" 
              icon="🎤" 
              items={stats.topArtists} 
              labelKey="name" 
              countKey="plays" 
              linkPrefix="/artist"
            />
          )}
          {stats.topOpeners && (
            <MiniTable 
              title="Top Openers" 
              icon="🚀" 
              items={stats.topOpeners} 
              labelKey="name" 
              countKey="count" 
              linkPrefix="/song"
            />
          )}
          {stats.topClosers && (
            <MiniTable 
              title="Top Closers" 
              icon="🎬" 
              items={stats.topClosers} 
              labelKey="name" 
              countKey="count" 
              linkPrefix="/song"
            />
          )}
          {stats.topSprints && (
            <MiniTable 
              title="Top Sprints" 
              icon="⚡" 
              items={stats.topSprints} 
              labelKey="name" 
              countKey="count" 
              linkPrefix="/song"
            />
          )}
          {stats.topSlowSongs && (
            <MiniTable 
              title="Top Slow Songs" 
              icon="🫠" 
              items={stats.topSlowSongs} 
              labelKey="name" 
              countKey="count" 
              linkPrefix="/song"
            />
          )}
        </div>
      </div>

      {/* Trending & Classics */}
      <div className="stats-section">
        <h2>🔥 Hot & Classic</h2>
        <div className="stats-grid">
          {stats.hotRightNow && (
            <div className="stat-highlight hot">
              <div className="highlight-icon">🔥</div>
              <div className="highlight-content">
                <div className="highlight-label">Hot Right Now (Last 30 Days)</div>
                <Link to={buildUrl(`/song/${stats.hotRightNow.id}`)} className="highlight-value">
                  {stats.hotRightNow.name}
                </Link>
                <div className="highlight-detail">
                  {formatArtists(stats.hotRightNow.artists)} • {stats.hotRightNow.recent_plays} recent plays
                </div>
              </div>
            </div>
          )}
          
          {stats.classicStillPlaying && (
            <div className="stat-highlight classic">
              <div className="highlight-icon">💎</div>
              <div className="highlight-content">
                <div className="highlight-label">Classic Still Going Strong</div>
                <Link to={buildUrl(`/song/${stats.classicStillPlaying.id}`)} className="highlight-value">
                  {stats.classicStillPlaying.name}
                </Link>
                <div className="highlight-detail">
                  First played {formatDate(stats.classicStillPlaying.first_played)} • 
                  Still spinning ({stats.classicStillPlaying.total_plays} total plays)
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* BPM Stats */}
      <div className="stats-section">
        <h2>💓 BPM Breakdown</h2>
        <div className="bpm-stats">
          <div className="bpm-overview">
            <div className="bpm-stat">
              <span className="bpm-value">{stats.bpmStats?.avg_bpm}</span>
              <span className="bpm-label">Average BPM</span>
            </div>
            <div className="bpm-stat">
              <span className="bpm-value">{stats.bpmStats?.min_bpm}</span>
              <span className="bpm-label">Lowest</span>
            </div>
            <div className="bpm-stat">
              <span className="bpm-value">{stats.bpmStats?.max_bpm}</span>
              <span className="bpm-label">Highest</span>
            </div>
          </div>
          
          {stats.bpmRanges && (
            <div className="bpm-distribution">
              <h4>BPM Distribution</h4>
              <div className="bpm-bars">
                {stats.bpmRanges.map((range) => (
                  <div key={range.range} className="bpm-bar-container">
                    <div 
                      className="bpm-bar" 
                      style={{ 
                        height: `${Math.min(100, (parseInt(range.count) / Math.max(...stats.bpmRanges.map(r => parseInt(r.count)))) * 100)}%` 
                      }}
                    >
                      <span className="bpm-bar-count">{range.count}</span>
                    </div>
                    <span className="bpm-bar-label">{range.range}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Role Stats */}
      <div className="stats-section">
        <h2>🎭 Song Roles</h2>
        <div className="role-stats">
          <div className="role-stat opener">
            <span className="role-count">{stats.roleStats?.total_openers || 0}</span>
            <span className="role-label">Opener Uses</span>
          </div>
          <div className="role-stat sprint">
            <span className="role-count">{stats.roleStats?.total_sprints || 0}</span>
            <span className="role-label">Sprint Uses</span>
          </div>
          <div className="role-stat slow">
            <span className="role-count">{stats.roleStats?.total_slow_songs || 0}</span>
            <span className="role-label">Slow Song Uses</span>
          </div>
          <div className="role-stat closer">
            <span className="role-count">{stats.roleStats?.total_closers || 0}</span>
            <span className="role-label">Closer Uses</span>
          </div>
        </div>
      </div>

      {/* Fun Facts */}
      <div className="stats-section">
        <h2>🎲 Fun Facts</h2>
        <div className="fun-facts">
          <div className="fun-fact">
            <span className="fact-emoji">1️⃣</span>
            <span className="fact-text">
              <strong>{stats.oneHitWonders?.toLocaleString()}</strong> songs have only been played once (one-hit wonders!)
            </span>
          </div>
          <div className="fun-fact">
            <span className="fact-emoji">😴</span>
            <span className="fact-text">
              <strong>{stats.neverPlayed?.toLocaleString()}</strong> songs in your library have never been played
            </span>
          </div>
          <div className="fun-fact">
            <span className="fact-emoji">📅</span>
            <span className="fact-text">
              First playlist: <strong>{formatDate(stats.dateRange?.first_playlist)}</strong> • 
              Latest: <strong>{formatDate(stats.dateRange?.last_playlist)}</strong>
            </span>
          </div>
          <div className="fun-fact">
            <span className="fact-emoji">📊</span>
            <span className="fact-text">
              Average <strong>{stats.avgPlaylistsPerMonth}</strong> playlists per month • 
              <strong>{stats.avgSongsPerPlaylist}</strong> songs per playlist
            </span>
          </div>
          {stats.topLetters && (
            <div className="fun-fact">
              <span className="fact-emoji">🔤</span>
              <span className="fact-text">
                Most common starting letters: {stats.topLetters.map(l => `${l.letter} (${l.count})`).join(', ')}
              </span>
            </div>
          )}
        </div>
      </div>

      <div className="stats-actions">
        <Link to={buildUrl('/')} className="btn-back">← Back to Playlists</Link>
      </div>
    </div>
  );
}

export default Stats;
